/**
 * B2B Cart Order Review
 * - Preserves Shopify cart updates and existing sync/auth flow.
 * - Adds grouped product metrics and labour/weight summaries.
 */
(function () {
  'use strict';

  /** @param {string} sel @param {ParentNode} [root] */
  function qs(sel, root) {
    return (root || document).querySelector(sel);
  }

  /** @param {string} value */
  function escCssIdent(value) {
    var s = String(value);
    if (typeof window !== 'undefined' && window.CSS && typeof window.CSS.escape === 'function') {
      return window.CSS.escape(s);
    }
    return s.replace(/([ !"#$%&'()*+,./:;<=>?@[\\\]^`{|}~])/g, '\\$1');
  }

  /** @param {(...args: unknown[]) => void} fn @param {number} wait */
  function debounced(fn, wait) {
    /** @type {number|undefined} */
    var t;
    /** @param {...unknown} args */
    function invoked() {
      var args = arguments;
      window.clearTimeout(t);
      t = window.setTimeout(function () {
        fn.apply(null, /** @type {unknown[]} */ (args));
      }, wait);
    }
    invoked.cancel = function () {
      window.clearTimeout(t);
    };
    return invoked;
  }

  /** @param {number} cents @param {string} code @param {boolean} withCode */
  function formatMoney(cents, code, withCode) {
    if (typeof cents !== 'number' || Number.isNaN(cents)) return '';
    var amount = cents / 100;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: code || 'USD',
        currencyDisplay: withCode ? 'code' : 'narrowSymbol',
      }).format(amount);
    } catch {
      return (code || '') + ' ' + amount.toFixed(2);
    }
  }

  /** @returns {Promise<{res: Response, data: Record<string, unknown>, txt: string}>} */
  async function fetchJson(url, opts) {
    var res = await fetch(url, {
      credentials: 'same-origin',
      ...(opts || {}),
      headers: {
        Accept: 'application/json',
        ...(opts && opts.headers),
      },
    });
    var txt = await res.text();
    /** @type {Record<string, unknown>} */
    var data = {};
    try {
      data = txt ? JSON.parse(txt) : {};
    } catch {
      data = {};
    }
    return { res, data, txt };
  }

  /** @param {Record<string, unknown>} cfg */
  function readToken(cfg) {
    try {
      var v = window.localStorage.getItem(String(cfg.tokenStorageKey || ''));
      return v != null ? String(v).trim() : '';
    } catch {
      return '';
    }
  }

  /** @param {Record<string, unknown>} cfg */
  function readProfile(cfg) {
    try {
      var raw = window.localStorage.getItem(String(cfg.profileStorageKey || ''));
      if (!raw) return null;
      var o = JSON.parse(raw);
      return o && typeof o === 'object' ? /** @type {Record<string, unknown>} */ (o) : null;
    } catch {
      return null;
    }
  }

  /** @param {Record<string, unknown>} cfg */
  function isSalesRepOk(cfg) {
    if (!cfg.salesRepValidate) return true;
    if (cfg.salesRepEmail) return true;
    if (cfg.customerLoggedInViaMetafieldOrCartAttr) return true;
    if (cfg.shopCustomer) return true;
    return false;
  }

  /** @param {string} base @param {string} path */
  function joinUrl(base, path) {
    var b = String(base || '').replace(/\/+$/, '');
    var p = String(path || '');
    if (!p) return b;
    if (/^https?:\/\//i.test(p)) return p;
    if (p.charAt(0) !== '/') p = '/' + p;
    return b ? b + p : p;
  }

  /** @param {string} url */
  function getStorefrontApiHeaders(url) {
    /** @type {Record<string, string>} */
    var headers = { Accept: 'application/json' };
    try {
      var parsed = new URL(url, window.location.origin);
      var host = (parsed.hostname || '').toLowerCase();
      if (host.indexOf('ngrok') >= 0) headers['ngrok-skip-browser-warning'] = 'true';
    } catch (_) {}
    return headers;
  }

  /** @param {string} url @param {RequestInit} [opts] */
  async function fetchApiJson(url, opts) {
    var apiHeaders = getStorefrontApiHeaders(url);
    var res = await fetch(url, {
      mode: 'cors',
      credentials: 'omit',
      ...(opts || {}),
      headers: {
        ...apiHeaders,
        ...(opts && opts.headers ? opts.headers : {}),
      },
    });
    var txt = await res.text();
    /** @type {Record<string, unknown>} */
    var data = {};
    try {
      data = txt ? JSON.parse(txt) : {};
    } catch {
      data = {};
    }
    return { res, data };
  }

  /** @param {string} raw */
  function splitCsv(raw) {
    return String(raw || '')
      .split(',')
      .map(function (p) {
        return p.trim();
      })
      .filter(Boolean);
  }

  /** @param {unknown} raw */
  function parseWeightNumber(raw) {
    if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
    var s = String(raw == null ? '' : raw).trim();
    if (!s) return null;
    var m = s.replace(/,/g, '').match(/-?\d+(\.\d+)?/);
    if (!m) return null;
    var n = Number(m[0]);
    return Number.isFinite(n) ? n : null;
  }

  /** @param {number} value */
  function roundWeightValue(value) {
    return Math.round((value + Number.EPSILON) * 100) / 100;
  }

  /** @param {number} value */
  function formatWeightValue(value) {
    return roundWeightValue(value).toFixed(2) + ' g';
  }

  /** @param {unknown} raw @param {number[]} bucket */
  function collectWeightNumbers(raw, bucket) {
    if (raw == null) return;

    if (typeof raw === 'number') {
      if (Number.isFinite(raw) && raw > 0) bucket.push(raw);
      return;
    }

    if (Array.isArray(raw)) {
      raw.forEach(function (entry) {
        collectWeightNumbers(entry, bucket);
      });
      return;
    }

    if (typeof raw === 'object') {
      var obj = /** @type {Record<string, unknown>} */ (raw);
      var listKeys = [
        'weights',
        'selectedWeights',
        'selected_weights',
        'values',
        'entries',
        'items',
        'data',
      ];
      var foundList = false;
      for (var i = 0; i < listKeys.length; i++) {
        var lk = listKeys[i];
        if (obj[lk] == null) continue;
        foundList = true;
        collectWeightNumbers(obj[lk], bucket);
      }
      if (foundList) return;

      var scalarWeight =
        obj.weight != null
          ? obj.weight
          : obj.value != null
            ? obj.value
            : obj.grams != null
              ? obj.grams
              : null;
      var parsed = parseWeightNumber(scalarWeight);
      if (parsed == null || !Number.isFinite(parsed) || parsed <= 0) return;
      var qtyRaw =
        obj.quantity != null
          ? obj.quantity
          : obj.qty != null
            ? obj.qty
            : obj.count != null
              ? obj.count
              : 1;
      var qty = Math.max(1, Math.floor(Number(qtyRaw) || 1));
      for (var q = 0; q < qty; q++) bucket.push(parsed);
      return;
    }

    var s = String(raw).trim();
    if (!s) return;
    if ((s.charAt(0) === '[' && s.charAt(s.length - 1) === ']') || (s.charAt(0) === '{' && s.charAt(s.length - 1) === '}')) {
      try {
        collectWeightNumbers(JSON.parse(s), bucket);
        return;
      } catch {
        /* treat as CSV fallback */
      }
    }
    splitCsv(s).forEach(function (part) {
      var n = parseWeightNumber(part);
      if (n != null && Number.isFinite(n) && n > 0) bucket.push(n);
    });
  }

  /** @param {number[]} entries */
  function buildGroupedWeights(entries) {
    /** @type {Record<string, { weight: number, quantity: number }>} */
    var groupedByKey = {};
    var totalCount = 0;
    entries.forEach(function (n) {
      if (!Number.isFinite(n) || n <= 0) return;
      var normalized = roundWeightValue(n);
      if (!Number.isFinite(normalized) || normalized <= 0) return;
      var key = normalized.toFixed(2);
      if (!groupedByKey[key]) groupedByKey[key] = { weight: normalized, quantity: 0 };
      groupedByKey[key].quantity += 1;
      totalCount += 1;
    });

    var grouped = Object.keys(groupedByKey)
      .map(function (k) {
        return groupedByKey[k];
      })
      .sort(function (a, b) {
        return a.weight - b.weight;
      });

    return { grouped: grouped, totalCount: totalCount };
  }

  /** @param {unknown} raw */
  function normalizeSelectedWeights(raw) {
    /** @type {number[]} */
    var entries = [];
    collectWeightNumbers(raw, entries);
    return buildGroupedWeights(entries);
  }

  /** @param {Record<string, unknown>} item */
  function extractRawSelectedWeight(item) {
    var props =
      item.properties && typeof item.properties === 'object'
        ? /** @type {Record<string, unknown>} */ (item.properties)
        : {};
    var keys = ['Weight', 'SelectedWeights', 'selected_weights', 'Weights', 'weight'];
    for (var i = 0; i < keys.length; i++) {
      var key = keys[i];
      if (props[key] != null && String(props[key]).trim()) return props[key];
    }
    return '';
  }

  function selectedWeightColumnCount(distinctCount) {
    var count = typeof distinctCount === 'number' ? distinctCount : Infinity;
    if (count < 6) return 1;
    if (window.matchMedia('(min-width: 1200px)').matches) return 4;
    if (window.matchMedia('(min-width: 768px)').matches) return 2;
    return 1;
  }

  /** @param {{ weight: number, quantity: number }[]} list @param {number} count */
  function splitIntoWeightColumns(list, count) {
    if (!list.length) return [];
    var columns = Math.max(1, Math.min(count, list.length));
    var base = Math.floor(list.length / columns);
    var remainder = list.length % columns;
    /** @type {{ weight: number, quantity: number }[][]} */
    var result = [];
    var start = 0;
    for (var i = 0; i < columns; i++) {
      var size = base + (i < remainder ? 1 : 0);
      var end = start + size;
      result.push(list.slice(start, end));
      start = end;
    }
    return result;
  }

  /**
   * @param {{ weight: number, quantity: number }[]} column
   * @param {number} srStart
   */
  function renderSelectedWeightColumnHtml(column, srStart) {
    var sr = srStart;
    var rowsHtml = column
      .map(function (entry) {
        var rowHtml =
          '<div class="b2b-cart-review__selected-weight-value-row">' +
          '<span class="b2b-cart-review__selected-weight-sr">' +
          sr +
          '</span>' +
          '<span class="b2b-cart-review__selected-weight-grams">' +
          formatWeightValue(entry.weight) +
          '</span>' +
          '<span class="b2b-cart-review__selected-weight-qty">' +
          entry.quantity +
          '</span>' +
          '</div>';
        sr += 1;
        return rowHtml;
      })
      .join('');

    return (
      '<div class="b2b-cart-review__selected-weight-column">' +
      '<div class="b2b-cart-review__selected-weight-column-head">' +
      '<span class="b2b-cart-review__selected-weight-sr">SR. NO</span>' +
      '<span class="b2b-cart-review__selected-weight-grams">WEIGHT (EACH)</span>' +
      '<span class="b2b-cart-review__selected-weight-qty">QTY</span>' +
      '</div>' +
      '<div class="b2b-cart-review__selected-weight-column-body">' +
      rowsHtml +
      '</div>' +
      '</div>'
    );
  }

  /** @param {HTMLElement} row */
  function getWeightUiEls(row) {
    var trigger = qs('[data-selected-weight-toggle]', row);
    var triggerCount = qs('[data-selected-weight-trigger-count]', row);
    var panelRow = null;

    if (row.nextElementSibling instanceof HTMLElement && row.nextElementSibling.matches('[data-selected-weight-panel-row]')) {
      panelRow = row.nextElementSibling;
    } else {
      var lineKey = row.dataset.lineKey || row.dataset.cartLineKey || '';
      var parent = row.parentElement;
      if (lineKey && parent) {
        panelRow = parent.querySelector(
          '[data-selected-weight-panel-row][data-panel-for-line="' + escCssIdent(lineKey) + '"]'
        );
      }
    }

    var panel = null;
    if (trigger instanceof HTMLElement) {
      var panelId = trigger.getAttribute('aria-controls') || '';
      if (panelId) panel = document.getElementById(panelId);
    }
    if (!(panel instanceof HTMLElement) && panelRow instanceof HTMLElement) {
      panel = qs('[data-selected-weight-panel]', panelRow);
    }
    if (!(panel instanceof HTMLElement)) {
      panel = qs('[data-selected-weight-panel]', row);
    }

    return {
      trigger: trigger,
      triggerCount: triggerCount,
      panelRow: panelRow instanceof HTMLElement ? panelRow : null,
      panel: panel instanceof HTMLElement ? panel : null,
    };
  }

  function isMobileWeightLayout() {
    return window.matchMedia('(max-width: 767px)').matches;
  }

  /** @param {HTMLElement} panel */
  function isWeightPanelHostedInItemRow(panel) {
    return !!(panel.closest && panel.closest('[data-cart-item]'));
  }

  /** @param {HTMLElement} root */
  function syncSelectedWeightPanelPlacement(root) {
    var mobile = isMobileWeightLayout();
    root.querySelectorAll('[data-cart-item]').forEach(function (row) {
      if (!(row instanceof HTMLElement)) return;
      var ui = getWeightUiEls(row);
      var panel = ui.panel;
      var panelRow = ui.panelRow;
      var cell = qs('.b2b-cart-review__selected-weights-cell', row);
      if (!(panel instanceof HTMLElement) || !(cell instanceof HTMLElement)) return;

      if (mobile) {
        if (panel.parentElement !== cell) cell.appendChild(panel);
        if (panelRow instanceof HTMLElement) {
          panelRow.hidden = true;
          panelRow.setAttribute('aria-hidden', 'true');
        }
        return;
      }

      if (!(panelRow instanceof HTMLElement)) return;
      var hostTd = panelRow.querySelector('td');
      if (!(hostTd instanceof HTMLElement)) return;
      if (panel.parentElement !== hostTd) hostTd.appendChild(panel);
      var expanded = panel.dataset.expanded === 'true' && !panel.hidden;
      panelRow.hidden = !expanded;
      panelRow.setAttribute('aria-hidden', expanded ? 'false' : 'true');
    });
  }

  /** @param {HTMLElement} panel */
  function stopPanelAnimation(panel) {
    var running = panel.__b2bPanelAnimation;
    if (running && typeof running.cancel === 'function') running.cancel();
    panel.__b2bPanelAnimation = null;
  }

  /**
   * @param {HTMLElement} panel
   * @param {boolean} expanded
   * @param {{ restoreFocus?: boolean }} [opts]
   */
  function animatePanelState(panel, expanded, opts) {
    var panelRow = panel.closest('[data-selected-weight-panel-row]');
    var hostedInItem = isWeightPanelHostedInItemRow(panel);
    var card = panel.closest('[data-b2b-cart-review]');
    var triggerId = panel.getAttribute('aria-labelledby') || '';
    var trigger = card ? qs('#' + escCssIdent(triggerId), card) : null;
    var inner = qs('[data-selected-weight-panel-inner]', panel);
    if (!(inner instanceof HTMLElement)) return;

    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var duration = reduced ? 0 : 240;

    stopPanelAnimation(panel);
    panel.dataset.expanded = expanded ? 'true' : 'false';
    panel.setAttribute('aria-hidden', expanded ? 'false' : 'true');
    if (panelRow instanceof HTMLElement) {
      if (hostedInItem) {
        panelRow.hidden = true;
        panelRow.setAttribute('aria-hidden', 'true');
      } else {
        panelRow.setAttribute('aria-hidden', expanded ? 'false' : 'true');
      }
    }
    if (trigger instanceof HTMLElement) trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');

    if (expanded) {
      if (panelRow instanceof HTMLElement && !hostedInItem) panelRow.hidden = false;
      panel.hidden = false;
      if (!duration) {
        inner.style.height = '';
        inner.style.opacity = '';
        inner.style.overflow = '';
        return;
      }
      inner.style.overflow = 'hidden';
      inner.style.height = '0px';
      inner.style.opacity = '0';
      panel.offsetHeight;
      var openTo = inner.scrollHeight;
      inner.style.transition = 'height ' + duration + 'ms ease, opacity ' + duration + 'ms ease';
      inner.style.height = openTo + 'px';
      inner.style.opacity = '1';
      var onOpenEnd = function onOpenEnd(ev) {
        if (ev.target !== inner || ev.propertyName !== 'height') return;
        inner.removeEventListener('transitionend', onOpenEnd);
        inner.style.transition = '';
        inner.style.height = '';
        inner.style.opacity = '';
        inner.style.overflow = '';
        panel.__b2bPanelAnimation = null;
      };
      inner.addEventListener('transitionend', onOpenEnd);
      panel.__b2bPanelAnimation = {
        cancel: function () {
          inner.removeEventListener('transitionend', onOpenEnd);
          inner.style.transition = '';
          inner.style.height = '';
          inner.style.opacity = '';
          inner.style.overflow = '';
        },
      };
      return;
    }

    if (!duration) {
      panel.hidden = true;
      if (panelRow instanceof HTMLElement && !hostedInItem) panelRow.hidden = true;
      inner.style.height = '';
      inner.style.opacity = '';
      inner.style.overflow = '';
      if (opts && opts.restoreFocus && trigger instanceof HTMLElement) trigger.focus();
      return;
    }

    var closeFrom = inner.scrollHeight;
    inner.style.overflow = 'hidden';
    inner.style.height = closeFrom + 'px';
    inner.style.opacity = '1';
    panel.offsetHeight;
    inner.style.transition = 'height ' + duration + 'ms ease, opacity ' + duration + 'ms ease';
    inner.style.height = '0px';
    inner.style.opacity = '0';
    var onCloseEnd = function onCloseEnd(ev) {
      if (ev.target !== inner || ev.propertyName !== 'height') return;
      inner.removeEventListener('transitionend', onCloseEnd);
      panel.hidden = true;
      if (panelRow instanceof HTMLElement && !hostedInItem) panelRow.hidden = true;
      inner.style.transition = '';
      inner.style.height = '';
      inner.style.opacity = '';
      inner.style.overflow = '';
      panel.__b2bPanelAnimation = null;
      if (opts && opts.restoreFocus && trigger instanceof HTMLElement) trigger.focus();
    };
    inner.addEventListener('transitionend', onCloseEnd);
    panel.__b2bPanelAnimation = {
      cancel: function () {
        inner.removeEventListener('transitionend', onCloseEnd);
        inner.style.transition = '';
        inner.style.height = '';
        inner.style.opacity = '';
        inner.style.overflow = '';
      },
    };
  }

  /** @param {HTMLElement} row @param {boolean} expanded @param {{restoreFocus?: boolean}} [opts] */
  function setSelectedWeightPanelState(row, expanded, opts) {
    var rootEl = row.closest('[data-b2b-cart-review]');
    if (rootEl instanceof HTMLElement) syncSelectedWeightPanelPlacement(rootEl);
    var ui = getWeightUiEls(row);
    if (!(ui.panel instanceof HTMLElement)) return;
    row.dataset.selectedWeightOpen = expanded ? '1' : '0';
    animatePanelState(ui.panel, expanded, opts);
  }

  /**
   * @param {HTMLElement} row
   * @param {{ grouped: { weight: number, quantity: number }[], totalCount: number }} normalized
   * @param {number} totalWeight
   */
  function renderSelectedWeightPanel(row, normalized, totalWeight) {
    var ui = getWeightUiEls(row);
    var trigger = ui.trigger;
    var triggerCount = ui.triggerCount;
    var panelRow = ui.panelRow;
    var panel = ui.panel;
    if (!(trigger instanceof HTMLButtonElement) || !(panel instanceof HTMLElement)) return;

    var titleCount = qs('[data-selected-weight-summary-count]', panel);
    var columnsHost = qs('[data-selected-weight-columns]', panel);
    if (!(columnsHost instanceof HTMLElement)) return;

    if (normalized.totalCount <= 0 || !normalized.grouped.length) {
      setSelectedWeightPanelState(row, false);
      trigger.hidden = true;
      trigger.setAttribute('aria-hidden', 'true');
      if (panelRow instanceof HTMLElement) {
        panelRow.hidden = true;
        panelRow.setAttribute('aria-hidden', 'true');
      }
      panel.hidden = true;
      panel.setAttribute('aria-hidden', 'true');
      return;
    }

    trigger.hidden = false;
    trigger.removeAttribute('aria-hidden');
    if (triggerCount) triggerCount.textContent = String(normalized.totalCount);
    if (titleCount) titleCount.textContent = String(normalized.totalCount);

    var distinctCount = normalized.grouped.length;
    var colCount = selectedWeightColumnCount(distinctCount);
    var columns = splitIntoWeightColumns(normalized.grouped, colCount);
    columnsHost.classList.toggle('b2b-cart-review__selected-weight-columns--single', colCount === 1);
    columnsHost.classList.toggle('b2b-cart-review__selected-weight-columns--multi', colCount > 1);
    columnsHost.style.setProperty('--bcr-weight-col-count', String(colCount));

    var srStart = 1;
    columnsHost.innerHTML = columns
      .map(function (column) {
        var html = renderSelectedWeightColumnHtml(column, srStart);
        srStart += column.length;
        return html;
      })
      .join('');

    if (row.dataset.selectedWeightOpen === '1') setSelectedWeightPanelState(row, true);
  }

  /** @param {Record<string, unknown>} item */
  function weightStatsFromItem(item) {
    var props =
      item.properties && typeof item.properties === 'object'
        ? /** @type {Record<string, unknown>} */ (item.properties)
        : {};
    var weight = props.Weight != null ? String(props.Weight).trim() : '';
    var total = 0;
    var min = Infinity;
    var max = -Infinity;
    var count = 0;

    if (weight) {
      splitCsv(weight).forEach(function (part) {
        var n = parseWeightNumber(part);
        if (n == null || !Number.isFinite(n) || n <= 0) return;
        count += 1;
        total += n;
        min = Math.min(min, n);
        max = Math.max(max, n);
      });
    }

    if (!count) {
      var grams = Number(item.grams || 0);
      var qty = Number(item.quantity || 0);
      if (Number.isFinite(grams) && grams > 0 && Number.isFinite(qty) && qty > 0) {
        total = (grams * qty) / 1000;
      }
    }

    return {
      count: count,
      min: count ? min : 0,
      max: count ? max : 0,
      total: total,
    };
  }

  /** @param {unknown} raw @param {boolean} [alreadyCents] */
  function parseMoneyCents(raw, alreadyCents) {
    if (typeof raw === 'number' && Number.isFinite(raw)) {
      if (alreadyCents) return Math.round(raw);
      return Math.round(raw * 100);
    }
    var s = String(raw == null ? '' : raw).trim();
    if (!s) return 0;
    var cleaned = s.replace(/[^0-9.,-]/g, '');
    if (!cleaned) return 0;
    var normalized = cleaned;
    if (cleaned.indexOf(',') >= 0 && cleaned.indexOf('.') >= 0) {
      normalized = cleaned.replace(/,/g, '');
    } else if (cleaned.indexOf(',') >= 0 && cleaned.indexOf('.') < 0) {
      normalized = cleaned.replace(/,/g, '.');
    }
    var num = Number(normalized);
    if (!Number.isFinite(num)) return 0;
    if (alreadyCents) return Math.round(num);
    return Math.round(num * 100);
  }

  /**
   * @param {Record<string, unknown>} item
   * @returns {{ hasValue: boolean, cents: number }}
   */
  function labourSourceFromItem(item) {
    var props =
      item.properties && typeof item.properties === 'object'
        ? /** @type {Record<string, unknown>} */ (item.properties)
        : {};

    var centKeys = [
      'LabourChargeCents',
      'Labour_Cents',
      'labor_charge_cents',
      'labour_charge_cents',
      'making_charge_cents',
    ];
    for (var i = 0; i < centKeys.length; i++) {
      var ck = centKeys[i];
      if (props[ck] != null && String(props[ck]).trim() !== '') {
        return { hasValue: true, cents: parseMoneyCents(props[ck], true) };
      }
    }

    var moneyKeys = [
      'Labour Charge',
      'Labour',
      'LabourCharge',
      'labor_charge',
      'Labor Charge',
      'making_charge',
      'Making Charge',
    ];
    for (var j = 0; j < moneyKeys.length; j++) {
      var mk = moneyKeys[j];
      if (props[mk] != null && String(props[mk]).trim() !== '') {
        return { hasValue: true, cents: parseMoneyCents(props[mk], false) };
      }
    }

    if (item.labour_charge_cents != null && String(item.labour_charge_cents).trim() !== '') {
      return { hasValue: true, cents: parseMoneyCents(item.labour_charge_cents, true) };
    }
    if (item.labor_charge_cents != null && String(item.labor_charge_cents).trim() !== '') {
      return { hasValue: true, cents: parseMoneyCents(item.labor_charge_cents, true) };
    }
    if (item.labour_charge != null && String(item.labour_charge).trim() !== '') {
      return { hasValue: true, cents: parseMoneyCents(item.labour_charge, false) };
    }
    if (item.labor_charge != null && String(item.labor_charge).trim() !== '') {
      return { hasValue: true, cents: parseMoneyCents(item.labor_charge, false) };
    }
    return { hasValue: false, cents: 0 };
  }

  /**
   * @param {Record<string, unknown>} item
   * @param {HTMLElement} [rowEl]
   * @returns {{ hasValue: boolean, cents: number }}
   */
  function labourSource(item, rowEl) {
    var primary = labourSourceFromItem(item);
    if (primary.hasValue) return primary;
    if (rowEl instanceof HTMLElement) {
      var fromLineData = rowEl.dataset.lineLabourCents;
      if (fromLineData != null && String(fromLineData).trim() !== '') {
        return { hasValue: true, cents: parseMoneyCents(fromLineData, true) };
      }
      var fromVariantData = rowEl.dataset.variantLabourCents;
      if (fromVariantData != null && String(fromVariantData).trim() !== '') {
        return { hasValue: true, cents: parseMoneyCents(fromVariantData, true) };
      }
      var lineLabourRaw = rowEl.dataset.lineLabour;
      if (lineLabourRaw != null && String(lineLabourRaw).trim() !== '') {
        return { hasValue: true, cents: parseMoneyCents(lineLabourRaw, false) };
      }
    }
    return primary;
  }

  /**
   * Shopify admin-configured unit price for this cart line variant.
   * @param {Record<string, unknown>} item
   * @param {HTMLElement} [rowEl]
   * @returns {{ hasValue: boolean, cents: number }}
   */
  function variantUnitPriceSource(item, rowEl) {
    var keys = ['original_price', 'price', 'final_price'];
    for (var i = 0; i < keys.length; i++) {
      var k = keys[i];
      var raw = item[k];
      if (typeof raw === 'number' && Number.isFinite(raw)) {
        return { hasValue: true, cents: Math.round(raw) };
      }
      if (raw != null && String(raw).trim() !== '') {
        var parsed = parseMoneyCents(raw, true);
        if (Number.isFinite(parsed)) return { hasValue: true, cents: parsed };
      }
    }
    if (rowEl instanceof HTMLElement) {
      var fromRow = rowEl.dataset.variantUnitPriceCents;
      if (fromRow != null && String(fromRow).trim() !== '') {
        return { hasValue: true, cents: parseMoneyCents(fromRow, true) };
      }
    }
    return { hasValue: false, cents: 0 };
  }

  function getGoldPricing() {
    return typeof window !== 'undefined' ? window.AoneGoldPricing : undefined;
  }

  /**
   * @param {Record<string, unknown>} item
   * @param {HTMLElement} [rowEl]
   */
  function jewelleryMaterialFromItem(item, rowEl) {
    var goldPricing = getGoldPricing();
    if (goldPricing && typeof goldPricing.resolveCartRowMaterial === 'function') {
      var resolved = goldPricing.resolveCartRowMaterial({
        properties:
          item.properties && typeof item.properties === 'object'
            ? /** @type {Record<string, unknown>} */ (item.properties)
            : {},
        options_with_values: item.options_with_values,
        row: rowEl || null,
        productTitle: item.product_title || item.title || '',
      });
      return resolved || '';
    }
    return '';
  }

  /**
   * @param {Record<string, unknown>} item
   * @param {HTMLElement} [rowEl]
   */
  function goldPropertiesFromSources(item, rowEl) {
    /** @type {Record<string, unknown>} */
    var props =
      item.properties && typeof item.properties === 'object'
        ? Object.assign({}, /** @type {Record<string, unknown>} */ (item.properties))
        : {};
    if (rowEl instanceof HTMLElement) {
      if (rowEl.dataset.goldPricePerTroyOz) props._gold_price_per_troy_oz = rowEl.dataset.goldPricePerTroyOz;
      if (rowEl.dataset.goldPriceCapturedAt) props._gold_price_captured_at = rowEl.dataset.goldPriceCapturedAt;
      if (rowEl.dataset.goldPriceMaterial) props._gold_price_material = rowEl.dataset.goldPriceMaterial;
      if (rowEl.dataset.goldPriceCurrency) props._gold_price_currency = rowEl.dataset.goldPriceCurrency;
      if (rowEl.dataset.goldPriceUnit) props._gold_price_unit = rowEl.dataset.goldPriceUnit;
    }
    return props;
  }

  var LINE_ADDED_AT_STORAGE_KEY = 'bcr_line_added_at_v1';

  /** @returns {Record<string, string>} */
  function readLineAddedAtMap() {
    if (typeof window === 'undefined' || !window.localStorage) return {};
    try {
      var raw = window.localStorage.getItem(LINE_ADDED_AT_STORAGE_KEY);
      if (!raw) return {};
      var parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? /** @type {Record<string, string>} */ (parsed) : {};
    } catch {
      return {};
    }
  }

  /** @param {Record<string, string>} map */
  function writeLineAddedAtMap(map) {
    if (typeof window === 'undefined' || !window.localStorage) return;
    try {
      window.localStorage.setItem(LINE_ADDED_AT_STORAGE_KEY, JSON.stringify(map));
    } catch {
      /* ignore quota errors */
    }
  }

  /** @param {Record<string, unknown>} props */
  function readAddedAtFromProperties(props) {
    if (!props || typeof props !== 'object') return '';
    var keys = ['_gold_price_captured_at', '_cart_added_at', '_added_at', 'added_at'];
    for (var i = 0; i < keys.length; i += 1) {
      var val = props[keys[i]];
      if (val != null && String(val).trim()) return String(val).trim();
    }
    return '';
  }

  /** @param {string} value */
  function isValidIsoTimestamp(value) {
    if (!value) return false;
    var d = new Date(value);
    return !Number.isNaN(d.getTime());
  }

  /**
   * @param {Record<string, unknown>} [item]
   * @param {HTMLElement} [rowEl]
   * @returns {string}
   */
  function resolveLineAddedAtIso(item, rowEl) {
    var props =
      item && item.properties && typeof item.properties === 'object'
        ? /** @type {Record<string, unknown>} */ (item.properties)
        : {};
    if (rowEl instanceof HTMLElement) {
      props = goldPropertiesFromSources(item || {}, rowEl);
    }

    var fromProps = readAddedAtFromProperties(props);
    if (isValidIsoTimestamp(fromProps)) return fromProps;

    var key =
      item && item.key != null
        ? String(item.key)
        : rowEl instanceof HTMLElement
          ? rowEl.dataset.lineKey || ''
          : '';
    if (key) {
      var map = readLineAddedAtMap();
      if (map[key] && isValidIsoTimestamp(String(map[key]))) return String(map[key]);
    }

    if (rowEl instanceof HTMLElement && rowEl.dataset.lineAddedAt && isValidIsoTimestamp(rowEl.dataset.lineAddedAt)) {
      return rowEl.dataset.lineAddedAt;
    }

    return '';
  }

  /**
   * @param {Record<string, unknown>} [item]
   * @param {HTMLElement} [rowEl]
   * @returns {string}
   */
  function ensureLineAddedAtIso(item, rowEl) {
    var existing = resolveLineAddedAtIso(item, rowEl);
    if (existing) return existing;

    var key =
      item && item.key != null
        ? String(item.key)
        : rowEl instanceof HTMLElement
          ? rowEl.dataset.lineKey || ''
          : '';
    if (!key) return '';

    var iso = new Date().toISOString();
    var map = readLineAddedAtMap();
    map[key] = iso;
    writeLineAddedAtMap(map);

    if (rowEl instanceof HTMLElement) {
      rowEl.dataset.lineAddedAt = iso;
    }

    return iso;
  }

  /**
   * @param {HTMLElement} root
   * @param {Record<string, unknown>[]} items
   * @param {Record<string, unknown>} cfg
   */
  function syncLineAddedAtMetadata(root, items, cfg) {
    if (!Array.isArray(items)) return;

    root.__bcrLineAddedAtPersist = root.__bcrLineAddedAtPersist || {};
    var persistQueue = /** @type {Promise<unknown>[]} */ ([]);

    items.forEach(function (item, idx) {
      var key = String(item.key || '');
      if (!key) return;

      var row = root.querySelector('[data-cart-item][data-line-key="' + escCssIdent(key) + '"]');
      var rowEl = row instanceof HTMLElement ? row : null;
      var props =
        item.properties && typeof item.properties === 'object'
          ? /** @type {Record<string, unknown>} */ (item.properties)
          : {};
      var fromProps = readAddedAtFromProperties(props);
      var iso = fromProps && isValidIsoTimestamp(fromProps) ? fromProps : ensureLineAddedAtIso(item, rowEl);

      if (rowEl && iso) {
        rowEl.dataset.lineAddedAt = iso;
      }

      if (!fromProps && iso && !root.__bcrLineAddedAtPersist[key] && cfg) {
        root.__bcrLineAddedAtPersist[key] = true;
        persistQueue.push(persistLineAddedAtProperty(idx + 1, item, iso, cfg, root));
      }
    });

    if (persistQueue.length) {
      Promise.all(persistQueue).catch(function () {
        /* localStorage fallback remains stable */
      });
    }
  }

  /**
   * @param {number} line
   * @param {Record<string, unknown>} item
   * @param {string} iso
   * @param {Record<string, unknown>} cfg
   * @param {HTMLElement} root
   */
  function persistLineAddedAtProperty(line, item, iso, cfg, root) {
    var props =
      item.properties && typeof item.properties === 'object'
        ? Object.assign({}, /** @type {Record<string, unknown>} */ (item.properties))
        : {};
    if (readAddedAtFromProperties(props)) return Promise.resolve(null);

    props._cart_added_at = iso;
    var qty = Number(item.quantity || 1);
    if (!Number.isFinite(qty) || qty < 1) qty = 1;

    return fetchJson(String(cfg.cartChangeJsUrl || '/cart/change.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line: line, quantity: qty, properties: props }),
    })
      .then(function (result) {
        if (!result.res.ok || result.data.errors) return null;
        var cart = /** @type {Record<string, unknown>} */ (result.data);
        if (Array.isArray(cart.items)) {
          root.__b2bLastCartItems = cart.items.map(function (it) {
            return /** @type {Record<string, unknown>} */ (it);
          });
        }
        return cart;
      })
      .catch(function () {
        return null;
      });
  }

  /**
   * Labour Per GM for a cart row.
   * Confirmed storefront source: Shopify variant unit price (previously shown as LABOUR CHARGE),
   * with optional line-item labour properties as a secondary source.
   * @param {Record<string, unknown>} item
   * @param {HTMLElement} [rowEl]
   * @returns {{ hasValue: boolean, cents: number }}
   */
  function labourChargeForRow(item, rowEl) {
    var fromVariant = variantUnitPriceSource(item, rowEl);
    if (fromVariant.hasValue) return fromVariant;
    return labourSource(item, rowEl);
  }

  /**
   * @param {HTMLElement} rowEl
   * @param {Record<string, unknown>} pricingResult
   * @param {string} code
   * @param {ReturnType<typeof getGoldPricing>} goldPricing
   * @param {Record<string, unknown>} [item]
   */
  function renderGoldPriceCell(rowEl, pricingResult, code, goldPricing, item) {
    var cell = qs('[data-line-gold-price]', rowEl);
    if (!cell) return;
    var rateEl = qs('[data-gold-price-rate]', cell);
    var tipBtn = qs('[data-gold-price-row-tip]', cell);
    var tipEl = qs('[data-gold-price-timestamp]', cell);
    if (!(rateEl instanceof HTMLElement)) return;

    var gold = /** @type {Record<string, unknown>} */ (pricingResult.gold || {});
    var karat =
      (goldPricing && typeof goldPricing.normalizeGoldKarat === 'function'
        ? goldPricing.normalizeGoldKarat(gold.karat || gold.material || rowEl.dataset.jewelryMaterial || '')
        : null) ||
      rowEl.dataset.goldKarat ||
      '';
    if (karat) rowEl.dataset.goldKarat = String(karat);

    var updatedAt =
      pricingResult.goldRateUpdatedAt != null
        ? String(pricingResult.goldRateUpdatedAt)
        : gold.updatedAt != null
          ? String(gold.updatedAt)
          : goldPricing && goldPricing.getStoreSnapshot
            ? String((goldPricing.getStoreSnapshot().lastUpdated || '') )
            : '';

    function hideGoldPriceTip() {
      if (tipEl instanceof HTMLElement) tipEl.textContent = '';
      if (tipBtn instanceof HTMLElement) {
        tipBtn.hidden = true;
        tipBtn.setAttribute('aria-hidden', 'true');
      }
    }

    function showGoldPriceTip(lines) {
      if (!(tipEl instanceof HTMLElement) || !(tipBtn instanceof HTMLElement)) return;
      tipEl.textContent = lines.join('\n');
      tipBtn.hidden = false;
      tipBtn.removeAttribute('aria-hidden');
    }

    if (pricingResult.status === 'loading') {
      rateEl.textContent = 'Loading…';
      hideGoldPriceTip();
      cell.dataset.goldPricingFallback = '0';
      cell.removeAttribute('aria-label');
      rowEl.removeAttribute('data-pricing-unavailable');
      return;
    }

    if (
      pricingResult.status === 'ok' &&
      pricingResult.goldPricePerGram != null &&
      Number.isFinite(Number(pricingResult.goldPricePerGram)) &&
      goldPricing
    ) {
      var goldPerGram = Number(pricingResult.goldPricePerGram);
      rateEl.textContent = goldPricing.formatPerGram
        ? goldPricing.formatPerGram(goldPerGram, code)
        : goldPricing.formatMoney(Math.round(goldPerGram * 100), code, false) + '/g';

      var tipParts = [];
      if (updatedAt && goldPricing.formatCapturedTimestamp) {
        var fullTs = goldPricing.formatCapturedTimestamp(updatedAt).replace(/^Added on\s+/i, '');
        if (fullTs) tipParts.push('Updated: ' + fullTs);
      } else if (updatedAt && goldPricing.formatLiveTickerTimestamp) {
        var liveTs = goldPricing.formatLiveTickerTimestamp(updatedAt);
        if (liveTs) tipParts.push('Updated: ' + liveTs);
      }

      if (tipParts.length) {
        showGoldPriceTip(tipParts);
        cell.setAttribute('aria-label', tipParts.join('. '));
      } else {
        hideGoldPriceTip();
        cell.removeAttribute('aria-label');
      }
      cell.dataset.goldPricingFallback = '0';
      rowEl.removeAttribute('data-pricing-unavailable');
      return;
    }

    rateEl.textContent = 'Unavailable';
    hideGoldPriceTip();
    cell.dataset.goldPricingFallback = '0';
    cell.setAttribute('aria-label', formatLivePricingUnavailableMessage());
    rowEl.dataset.pricingUnavailable = '1';
  }

  /**
   * @param {HTMLElement} rowEl
   * @param {Record<string, unknown>} pricingResult
   * @param {string} code
   * @param {boolean} withCode
   * @param {ReturnType<typeof getGoldPricing>} goldPricing
   */
  function renderLabourUnitAndTotalCells(rowEl, pricingResult, code, withCode, goldPricing) {
    var labourDisplay = qs('[data-line-labour-display]', rowEl);
    var unitPriceCell = qs('[data-line-unit-price]', rowEl);
    var priceCell = qs('[data-line-price]', rowEl);

    if (labourDisplay instanceof HTMLElement) {
      if (pricingResult.labourPerGram != null && Number.isFinite(Number(pricingResult.labourPerGram)) && goldPricing) {
        labourDisplay.textContent = goldPricing.formatMoney(
          Math.round(Number(pricingResult.labourPerGram) * 100),
          code,
          withCode
        );
      }
    }

    if (unitPriceCell instanceof HTMLElement) {
      unitPriceCell.classList.remove('b2b-cart-review__value--unavailable');
      if (pricingResult.status === 'loading') {
        unitPriceCell.textContent = 'Loading…';
      } else if (
        pricingResult.status === 'ok' &&
        pricingResult.unitPricePerGram != null &&
        Number.isFinite(Number(pricingResult.unitPricePerGram)) &&
        goldPricing
      ) {
        unitPriceCell.textContent = goldPricing.formatPerGram
          ? goldPricing.formatPerGram(Number(pricingResult.unitPricePerGram), code)
          : goldPricing.formatMoney(Math.round(Number(pricingResult.unitPricePerGram) * 100), code, false) + '/g';
      } else {
        unitPriceCell.textContent = formatUnavailableMoneyLabel();
        unitPriceCell.classList.add('b2b-cart-review__value--unavailable');
      }
    }

    if (priceCell instanceof HTMLElement) {
      priceCell.classList.remove('b2b-cart-review__value--unavailable');
      if (pricingResult.status === 'loading') {
        priceCell.textContent = 'Loading…';
        delete rowEl.dataset.calculatedRowTotalCents;
      } else if (pricingResult.status === 'ok' && pricingResult.rowTotalCents != null && goldPricing) {
        priceCell.textContent = goldPricing.formatMoney(pricingResult.rowTotalCents, code, withCode);
        rowEl.dataset.calculatedRowTotalCents = String(pricingResult.rowTotalCents);
      } else {
        priceCell.textContent = formatUnavailableMoneyLabel();
        priceCell.classList.add('b2b-cart-review__value--unavailable');
        delete rowEl.dataset.calculatedRowTotalCents;
      }
    }
  }

  function resolveRowWeightGrams(item, rowEl) {
    if (item) {
      var stats = weightStatsFromItem(item);
      if (Number.isFinite(stats.total) && stats.total > 0) return stats.total;
    }
    if (rowEl instanceof HTMLElement) {
      if (rowEl.dataset.selectedWeight) {
        var fromProp = weightStatsFromItem({ properties: { Weight: rowEl.dataset.selectedWeight } });
        if (Number.isFinite(fromProp.total) && fromProp.total > 0) return fromProp.total;
      }
      var totalWeightCell = qs('[data-row-total-weight]', rowEl);
      var goldPricing = getGoldPricing();
      var parsedFromDom = goldPricing
        ? goldPricing.parseWeightInGrams(totalWeightCell ? totalWeightCell.textContent : '')
        : parseWeightNumber(totalWeightCell ? totalWeightCell.textContent : '');
      if (parsedFromDom != null && parsedFromDom > 0) return parsedFromDom;
    }
    return null;
  }

  function resolveRowLabourCents(item, rowEl) {
    var labour = labourChargeForRow(item || {}, rowEl);
    if (labour.hasValue && Number.isFinite(labour.cents)) {
      return { cents: labour.cents, resolved: true };
    }
    return { cents: null, resolved: false };
  }

  function formatUnavailableMoneyLabel() {
    return 'Unavailable';
  }

  function formatLivePricingUnavailableMessage() {
    return 'Live gold pricing is currently unavailable. Please refresh or try again.';
  }

  /**
   * @param {HTMLElement} root
   * @param {Record<string, unknown>} cfg
   * @param {Record<string, unknown>[]} [items]
   */
  function recalculateCartPricing(root, cfg, items) {
    var goldPricing = getGoldPricing();
    var code =
      typeof cfg.currencyCode === 'string' && cfg.currencyCode
        ? cfg.currencyCode
        : goldPricing
          ? goldPricing.getActiveCurrencyCode()
          : 'USD';
    var withCode = !!cfg.currencyCodeEnabled;
    var cartItems = Array.isArray(items) ? items : root.__b2bLastCartItems || [];
    if (Array.isArray(items)) root.__b2bLastCartItems = items;

    /** @type {Record<string, Record<string, unknown>>} */
    var itemByKey = {};
    cartItems.forEach(function (item) {
      var key = String(item.key || '');
      if (key) itemByKey[key] = item;
    });

    /** @type {Record<string, { qty: number, weight: number, subtotal: number }>} */
    var byProduct = {};
    var orderTotalCents = 0;
    var invalidCount = 0;
    var loadingCount = 0;
    var qtyGrand = 0;
    var weightGrand = 0;
    var debugPricing =
      typeof window !== 'undefined' && /(?:^|[?&])bcr_debug=1(?:&|$)/.test(window.location.search || '');

    /** @type {Record<string, number>} */
    var srByProduct = {};

    root.querySelectorAll('[data-cart-item]').forEach(function (row) {
      if (!(row instanceof HTMLElement)) return;
      var key = row.dataset.lineKey || '';
      var item = key && itemByKey[key] ? itemByKey[key] : null;
      var props = goldPropertiesFromSources(item || {}, row);
      var material = jewelleryMaterialFromItem(item || {}, row);
      var karat =
        goldPricing && typeof goldPricing.normalizeGoldKarat === 'function'
          ? goldPricing.normalizeGoldKarat(material || row.dataset.jewelryMaterial || '')
          : null;
      if (karat) row.dataset.goldKarat = karat;

      var pid = row.dataset.productId || '';
      if (pid) {
        srByProduct[pid] = (srByProduct[pid] || 0) + 1;
        row.dataset.rowSr = String(srByProduct[pid]);
        var srCell = qs('[data-row-sr-display]', row);
        if (srCell) srCell.textContent = String(srByProduct[pid]);
      }

      var totalWeightGrams = resolveRowWeightGrams(item, row);
      var labourInfo = resolveRowLabourCents(item, row);

      /** @type {Record<string, unknown>} */
      var pricingResult = { status: 'unavailable', gold: { status: 'unavailable' } };
      if (goldPricing) {
        pricingResult = goldPricing.calculateRowPricing({
          properties: props,
          rowMaterial: material,
          totalWeightGrams: totalWeightGrams,
          labourCents: labourInfo.cents,
          labourResolved: labourInfo.resolved,
          cartCurrency: code,
          preferLive: true,
          allowFallback: false,
        });
      }

      if (pricingResult.status === 'loading') {
        loadingCount += 1;
        row.removeAttribute('data-pricing-unavailable');
      } else if (pricingResult.status === 'ok' && pricingResult.rowTotalCents != null) {
        row.removeAttribute('data-pricing-unavailable');
      } else {
        invalidCount += 1;
        row.dataset.pricingUnavailable = '1';
      }

      renderLabourUnitAndTotalCells(row, pricingResult, code, withCode, goldPricing);
      renderGoldPriceCell(row, pricingResult, code, goldPricing, item || undefined);

      if (debugPricing && typeof console !== 'undefined' && typeof console.debug === 'function') {
        var gold = pricingResult.gold || {};
        console.debug('[SyncCart Pricing]', {
          lineKey: key,
          productTitle: item && (item.product_title || item.title),
          resolvedMaterial: material,
          goldKarat: karat,
          liveGoldPricePerGram: pricingResult.goldPricePerGram,
          labourPerGram: pricingResult.labourPerGram,
          unitPricePerGram: pricingResult.unitPricePerGram,
          totalWeight: totalWeightGrams,
          liveGoldPricePerTroyOz: gold.perTroyOz,
          result: pricingResult.status,
        });
      }

      var qty = Number(item && item.quantity != null ? item.quantity : row.dataset.quantity || 0);
      if (!Number.isFinite(qty)) qty = 0;
      qtyGrand += qty;
      weightGrand += totalWeightGrams != null && Number.isFinite(totalWeightGrams) ? totalWeightGrams : 0;

      if (pid) {
        if (!byProduct[pid]) byProduct[pid] = { qty: 0, weight: 0, subtotal: 0 };
        var metric = byProduct[pid];
        metric.qty += qty;
        metric.weight += totalWeightGrams != null && Number.isFinite(totalWeightGrams) ? totalWeightGrams : 0;
        if (pricingResult.status === 'ok' && pricingResult.rowTotalCents != null) {
          metric.subtotal += pricingResult.rowTotalCents;
          orderTotalCents += pricingResult.rowTotalCents;
        }
      }
    });

    root.querySelectorAll('[data-group-metrics]').forEach(function (metricEl) {
      if (!(metricEl instanceof HTMLElement)) return;
      var pid = metricEl.dataset.groupProductId || '';
      var metric = byProduct[pid] || { qty: 0, weight: 0, subtotal: 0 };
      var qtyEl = qs('[data-group-total-qty]', metricEl);
      var weightEl = qs('[data-group-total-weight]', metricEl);
      var subtotalEl = qs('[data-group-subtotal-cents]', metricEl);
      if (qtyEl) qtyEl.textContent = String(metric.qty);
      if (weightEl) weightEl.textContent = metric.weight > 0 ? metric.weight.toFixed(2) + ' g' : '-';
      if (subtotalEl) {
        subtotalEl.classList.remove('b2b-cart-review__metric-value--unavailable');
        if (loadingCount > 0) {
          subtotalEl.textContent = 'Loading…';
        } else if (metric.subtotal > 0 && goldPricing) {
          subtotalEl.textContent = goldPricing.formatMoney(metric.subtotal, code, withCode);
        } else if (invalidCount > 0) {
          subtotalEl.textContent = formatUnavailableMoneyLabel();
          subtotalEl.classList.add('b2b-cart-review__metric-value--unavailable');
        } else {
          subtotalEl.textContent = goldPricing ? goldPricing.formatMoney(0, code, withCode) : formatMoney(0, code, withCode);
        }
      }
    });

    var totalQtyEl = qs('[data-order-summary-total-qty]', root);
    if (totalQtyEl) totalQtyEl.textContent = String(qtyGrand);

    var totalWeightEl = qs('[data-order-summary-total-weight]', root);
    if (totalWeightEl) {
      totalWeightEl.textContent = weightGrand > 0 ? weightGrand.toFixed(2) + ' g' : '-';
    }

    var totalEl = qs('[data-order-summary-total]', root);
    if (totalEl) {
      totalEl.classList.remove('b2b-cart-review__metric-value--unavailable');
      if (loadingCount > 0) {
        totalEl.textContent = 'Loading…';
      } else if (invalidCount > 0) {
        totalEl.textContent = '-';
        totalEl.classList.add('b2b-cart-review__metric-value--unavailable');
      } else if (goldPricing) {
        totalEl.textContent = goldPricing.formatMoney(orderTotalCents, code, withCode);
      } else {
        totalEl.textContent = formatMoney(orderTotalCents, code, withCode);
      }
    }

    var warnEl = qs('[data-order-pricing-warning]', root);
    if (warnEl instanceof HTMLElement) {
      if (invalidCount > 0 && loadingCount === 0) {
        warnEl.textContent = formatLivePricingUnavailableMessage();
        warnEl.hidden = false;
        warnEl.setAttribute('aria-hidden', 'false');
      } else {
        warnEl.hidden = true;
        warnEl.setAttribute('aria-hidden', 'true');
      }
    }

    root.__b2bPricingInvalid = invalidCount > 0;
    root.__b2bPricingLoading = loadingCount > 0;
    root.__b2bComputedOrderTotalCents = invalidCount > 0 || loadingCount > 0 ? 0 : orderTotalCents;
    updateSalesRepGate(root, cfg);
  }

  /**
   * @param {Record<string, unknown>} cart
   * @param {Record<string, unknown>} cfg
   * @param {HTMLElement} [root]
   */
  function validateCartPricing(cart, cfg, root) {
    var items = Array.isArray(cart.items) ? cart.items : [];
    if (!items.length) return 'Your cart is empty.';
    var goldPricing = getGoldPricing();
    if (!goldPricing) return 'Gold pricing is unavailable. Please refresh and try again.';

    var code =
      typeof cfg.currencyCode === 'string' && cfg.currencyCode
        ? cfg.currencyCode
        : goldPricing.getActiveCurrencyCode();

    for (var i = 0; i < items.length; i++) {
      var item = /** @type {Record<string, unknown>} */ (items[i]);
      var props =
        item.properties && typeof item.properties === 'object'
          ? /** @type {Record<string, unknown>} */ (item.properties)
          : {};
      var material = jewelleryMaterialFromItem(item);
      var totalWeightGrams = resolveRowWeightGrams(item);
      var labourInfo = resolveRowLabourCents(item);
      var pricingResult = goldPricing.calculateRowPricing({
        properties: props,
        rowMaterial: material,
        totalWeightGrams: totalWeightGrams,
        labourCents: labourInfo.cents,
        labourResolved: labourInfo.resolved,
        cartCurrency: code,
        preferLive: true,
        allowFallback: false,
      });
      if (pricingResult.status === 'loading') {
        return 'Gold pricing is still loading. Please wait a moment and try again.';
      }
      if (pricingResult.status !== 'ok') {
        return formatLivePricingUnavailableMessage();
      }
    }

    if (root instanceof HTMLElement && (root.__b2bPricingInvalid || root.__b2bPricingLoading)) {
      return formatLivePricingUnavailableMessage();
    }
    return null;
  }

  /** @param {HTMLElement} root */
  function getProductDetailsStateStore(root) {
    var existing = root.__b2bOpenProductDetails;
    if (existing && typeof existing === 'object') return existing;
    root.__b2bOpenProductDetails = {};
    return root.__b2bOpenProductDetails;
  }

  /** @param {HTMLButtonElement} toggle @param {boolean} expanded */
  function syncProductDetailsToggleVisual(toggle, expanded) {
    toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    var label = qs('.b2b-cart-review__details-toggle-label', toggle);
    if (label) label.textContent = expanded ? 'Hide Details' : 'Open Details';
  }

  /** @param {HTMLElement} root @param {boolean} disabled */
  function setProductDetailsButtonsDisabled(root, disabled) {
    root.querySelectorAll('[data-product-details-toggle]').forEach(function (btn) {
      if (!(btn instanceof HTMLButtonElement)) return;
      btn.disabled = disabled;
      if (disabled) btn.setAttribute('aria-busy', 'true');
      else btn.removeAttribute('aria-busy');
    });
  }

  /** @param {HTMLElement} panel */
  function stopProductDetailsAnimation(panel) {
    var running = panel.__b2bProductDetailsAnimation;
    if (running && typeof running.cancel === 'function') running.cancel();
    panel.__b2bProductDetailsAnimation = null;
  }

  /**
   * @param {HTMLElement} card
   * @param {boolean} expanded
   * @param {{ immediate?: boolean }} [opts]
   */
  function setProductDetailsState(card, expanded, opts) {
    var toggle = qs('[data-product-details-toggle]', card);
    var panel = qs('[data-product-details]', card);
    var inner = qs('[data-product-details-inner]', panel || card);
    if (!(toggle instanceof HTMLButtonElement) || !(panel instanceof HTMLElement) || !(inner instanceof HTMLElement)) return;

    var root = card.closest('[data-b2b-cart-review]');
    if (root instanceof HTMLElement) {
      var pid = card.dataset.productId || '';
      if (pid) {
        var store = getProductDetailsStateStore(root);
        if (expanded) store[pid] = 1;
        else delete store[pid];
      }
    }

    syncProductDetailsToggleVisual(toggle, expanded);
    panel.setAttribute('aria-hidden', expanded ? 'false' : 'true');
    card.dataset.detailsOpen = expanded ? '1' : '0';

    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var immediate = !!(opts && opts.immediate) || reduced;
    var duration = immediate ? 0 : 240;

    stopProductDetailsAnimation(panel);

    if (expanded) {
      panel.hidden = false;
      if (!duration) {
        inner.style.height = '';
        inner.style.opacity = '';
        inner.style.overflow = '';
        return;
      }
      inner.style.overflow = 'hidden';
      inner.style.height = '0px';
      inner.style.opacity = '0';
      panel.offsetHeight;
      var openTo = inner.scrollHeight;
      inner.style.transition = 'height ' + duration + 'ms ease, opacity ' + duration + 'ms ease';
      inner.style.height = openTo + 'px';
      inner.style.opacity = '1';
      var onOpenEnd = function onOpenEnd(ev) {
        if (ev.target !== inner || ev.propertyName !== 'height') return;
        inner.removeEventListener('transitionend', onOpenEnd);
        inner.style.transition = '';
        inner.style.height = '';
        inner.style.opacity = '';
        inner.style.overflow = '';
        panel.__b2bProductDetailsAnimation = null;
      };
      inner.addEventListener('transitionend', onOpenEnd);
      panel.__b2bProductDetailsAnimation = {
        cancel: function () {
          inner.removeEventListener('transitionend', onOpenEnd);
          inner.style.transition = '';
          inner.style.height = '';
          inner.style.opacity = '';
          inner.style.overflow = '';
        },
      };
      return;
    }

    if (!duration) {
      panel.hidden = true;
      inner.style.height = '';
      inner.style.opacity = '';
      inner.style.overflow = '';
      return;
    }

    var closeFrom = inner.scrollHeight;
    inner.style.overflow = 'hidden';
    inner.style.height = closeFrom + 'px';
    inner.style.opacity = '1';
    panel.offsetHeight;
    inner.style.transition = 'height ' + duration + 'ms ease, opacity ' + duration + 'ms ease';
    inner.style.height = '0px';
    inner.style.opacity = '0';
    var onCloseEnd = function onCloseEnd(ev) {
      if (ev.target !== inner || ev.propertyName !== 'height') return;
      inner.removeEventListener('transitionend', onCloseEnd);
      panel.hidden = true;
      inner.style.transition = '';
      inner.style.height = '';
      inner.style.opacity = '';
      inner.style.overflow = '';
      panel.__b2bProductDetailsAnimation = null;
    };
    inner.addEventListener('transitionend', onCloseEnd);
    panel.__b2bProductDetailsAnimation = {
      cancel: function () {
        inner.removeEventListener('transitionend', onCloseEnd);
        inner.style.transition = '';
        inner.style.height = '';
        inner.style.opacity = '';
        inner.style.overflow = '';
      },
    };
  }

  /** @param {HTMLElement} root */
  function reconcileProductDetails(root) {
    var state = getProductDetailsStateStore(root);
    /** @type {Record<string, 1>} */
    var alive = {};

    root.querySelectorAll('[data-product-group]').forEach(function (card) {
      if (!(card instanceof HTMLElement)) return;
      var pid = String(card.dataset.productId || '').trim();
      if (pid) alive[pid] = 1;
      var shouldOpen = !!(pid && state[pid] === 1);
      setProductDetailsState(card, shouldOpen, { immediate: true });
    });

    Object.keys(state).forEach(function (pid) {
      if (!alive[pid]) delete state[pid];
    });
  }

  /**
   * @param {Record<string, unknown>} cart
   * @returns {string|null}
   */
  function validateCartLinesForSync(cart) {
    var items = Array.isArray(cart.items) ? cart.items : [];
    if (!items.length) return 'Your cart is empty.';
    for (var i = 0; i < items.length; i++) {
      var item = /** @type {Record<string, unknown>} */ (items[i]);
      var props =
        item.properties && typeof item.properties === 'object'
          ? /** @type {Record<string, unknown>} */ (item.properties)
          : {};
      var weight = props.Weight != null ? String(props.Weight).trim() : '';
      var serials = props.SerialNumbers != null ? String(props.SerialNumbers).trim() : '';
      var qty = Number(item.quantity || 0);
      if (!weight || !serials) {
        return 'Select weight and serial for every item before syncing.';
      }
      if (splitCsv(weight).length !== qty || splitCsv(serials).length !== qty) {
        return 'Weight and serial selections must match quantity for each item.';
      }
    }
    return null;
  }

  /** @param {Record<string, unknown>} cfg */
  function showToast(cfg, message, type) {
    var Toast = typeof window !== 'undefined' ? window.AonegndToast : undefined;
    if (Toast && typeof Toast.show === 'function') {
      Toast.show(message, type);
      return;
    }
    if (type === 'error' && cfg.syncErrorFallback) {
      window.alert(message || String(cfg.syncErrorFallback));
    }
  }

  /** @param {Record<string, unknown>} item */
  function lineImageUrl(item) {
    if (typeof item.image === 'string') return item.image;
    if (item.image && typeof item.image === 'object' && 'src' in item.image) {
      return String(/** @type {{src?: string}} */ (item.image).src || '');
    }
    var fi = item.featured_image;
    if (typeof fi === 'string') return fi;
    if (fi && typeof fi === 'object' && 'url' in fi)
      return String(/** @type {{url?: string}} */ (fi).url || '');
    return '';
  }

  /** @param {Record<string, unknown>} item */
  function mapLineItem(item) {
    return {
      product_id: item.product_id,
      variant_id: item.variant_id,
      sku: item.sku != null ? String(item.sku) : '',
      title: item.title,
      product_title: item.product_title,
      variant_title: item.variant_title,
      quantity: item.quantity,
      price: item.price,
      final_price: item.final_price != null ? item.final_price : item.price,
      line_price: item.line_price,
      final_line_price: item.final_line_price != null ? item.final_line_price : item.line_price,
      image: lineImageUrl(item),
      url: item.url,
      properties: item.properties,
    };
  }

  /** @param {Record<string, unknown>} cart @param {Record<string, unknown>} cfg @param {string} idem @param {{id:number|string,name:string}|null} [selectedCustomer] @param {HTMLElement} [root] */
  function buildSyncPayload(cart, cfg, idem, selectedCustomer, root) {
    var token = readToken(cfg);
    var profile = readProfile(cfg);

    var isoCurrency =
      typeof cart.currency === 'string'
        ? cart.currency
        : cart.currency &&
            typeof cart.currency === 'object' &&
            cart.currency.iso_code != null &&
            typeof cart.currency.iso_code === 'string'
          ? cart.currency.iso_code
          : cfg.currencyCode;

    var items = Array.isArray(cart.items) ? cart.items.map(function (it) {
      var mapped = mapLineItem(/** @type {Record<string, unknown>} */ (it));
      var source = /** @type {Record<string, unknown>} */ (it);
      var props =
        source.properties && typeof source.properties === 'object'
          ? /** @type {Record<string, unknown>} */ (source.properties)
          : {};
      var goldPricing = getGoldPricing();
      if (goldPricing) {
        var material = jewelleryMaterialFromItem(source);
        var weights = weightStatsFromItem(source);
        var labour = labourChargeForRow(source);
        var pricingResult = goldPricing.calculateRowPricing({
          properties: props,
          rowMaterial: material,
          totalWeightGrams: weights.total,
          labourCents: labour.hasValue ? labour.cents : 0,
          labourResolved: labour.hasValue,
          cartCurrency: isoCurrency || String(cfg.currencyCode || 'USD'),
          preferLive: true,
          allowFallback: false,
        });
        mapped.computed_row_total_cents =
          pricingResult.status === 'ok' && pricingResult.rowTotalCents != null ? pricingResult.rowTotalCents : null;
        mapped.gold_karat =
          goldPricing.normalizeGoldKarat
            ? goldPricing.normalizeGoldKarat(material)
            : material;
        mapped.live_gold_price_per_gram =
          pricingResult.status === 'ok' && pricingResult.goldPricePerGram != null
            ? pricingResult.goldPricePerGram
            : null;
        mapped.labour_per_gram =
          pricingResult.labourPerGram != null ? pricingResult.labourPerGram : labour.hasValue ? labour.cents / 100 : null;
        mapped.unit_price_per_gram =
          pricingResult.status === 'ok' && pricingResult.unitPricePerGram != null
            ? pricingResult.unitPricePerGram
            : null;
        mapped.gold_rate_updated_at =
          pricingResult.goldRateUpdatedAt != null ? String(pricingResult.goldRateUpdatedAt) : '';
        mapped.captured_gold_price_per_troy_oz = props._gold_price_per_troy_oz != null ? String(props._gold_price_per_troy_oz) : '';
        mapped.captured_gold_price_material = props._gold_price_material != null ? String(props._gold_price_material) : material;
        mapped.captured_gold_price_captured_at =
          props._gold_price_captured_at != null ? String(props._gold_price_captured_at) : '';
      }
      return mapped;
    }) : [];

    var customerRaw = selectedCustomer || cfg.shopCustomer;
    /** @type {{id: string, email: string, name: string} | null} */
    var custOut = null;
    if (customerRaw && typeof customerRaw === 'object') {
      var c = /** @type {Record<string, unknown>} */ (customerRaw);
      custOut = {
        id: c.id != null ? String(c.id) : '',
        email: c.email != null ? String(c.email) : '',
        name: c.name != null ? String(c.name) : '',
      };
    }

    /** @type {Record<string, unknown>} */
    var out = {
      source: 'shopify_theme_cart',
      idempotency_key: idem,
      shopify_cart_token: cart.token,
      currency: isoCurrency || cart.currency,
      subtotal_price: cart.items_subtotal_price,
      total_price: cart.total_price,
      // TODO: Backend must recalculate order totals from captured gold rate, selected weight, material, and labour - do not trust browser totals alone.
      computed_total_cents:
        root instanceof HTMLElement && typeof root.__b2bComputedOrderTotalCents === 'number'
          ? root.__b2bComputedOrderTotalCents
          : null,
      item_count: cart.item_count,
      note: cart.note,
      attributes: cart.attributes || {},
      sales_rep: {
        token_present: !!token,
        profile: profile || {},
      },
      customer: custOut,
      items: items,
    };
    return out;
  }

  /** @param {HTMLElement} root @param {Record<string, unknown>} cfg */
  function updateSalesRepGate(root, cfg) {
    var ok = isSalesRepOk(cfg);
    var warn = qs('[data-b2b-login-warning]', root);
    var adminDisabled = !!cfg.adminDisableSync;
    if (warn) {
      warn.hidden = adminDisabled || !cfg.salesRepValidate || ok;
      warn.setAttribute('aria-hidden', warn.hidden ? 'true' : 'false');
    }
    var btn = qs('[data-sync-button]', root);
    if (!btn || !(btn instanceof HTMLButtonElement)) return;
    var labelEl = qs('[data-sync-button-label]', root);
    var hasItems = !!qs('[data-cart-item]', root);

    if (adminDisabled) {
      btn.disabled = true;
      btn.setAttribute('aria-disabled', 'true');
      btn.setAttribute('data-admin-sync-disabled', 'true');
      btn.classList.add('b2b-cart-review__sync-button--admin-disabled');
      btn.classList.remove('is-loading');
      if (labelEl) {
        labelEl.textContent = String(cfg.adminDisableSyncText || 'Admin disabled');
      }
      return;
    }

    if (btn.getAttribute('data-admin-sync-disabled') === 'true') {
      btn.removeAttribute('data-admin-sync-disabled');
      btn.removeAttribute('aria-disabled');
      btn.classList.remove('b2b-cart-review__sync-button--admin-disabled');
      if (labelEl && cfg.syncButtonTextDefault != null && !btn.classList.contains('is-loading')) {
        labelEl.textContent = String(cfg.syncButtonTextDefault);
      }
    }

    if (!hasItems) {
      btn.disabled = true;
      return;
    }
    if (cfg.salesRepValidate && cfg.disableSyncWhenNotLogged && !ok) {
      btn.disabled = true;
      return;
    }
    if (root.__b2bPricingInvalid || root.__b2bPricingLoading) {
      btn.disabled = true;
      return;
    }
    btn.disabled = false;
  }

  /** @param {HTMLElement} root @param {Record<string, unknown>[]} items @param {Record<string, unknown>} cfg */
  function applyComputedMetrics(root, items, cfg) {
    recalculateCartPricing(root, cfg, items);
  }

  /** @param {HTMLElement} root @param {Record<string, unknown>[]} items @param {string} code @param {boolean} withCode @param {number} subtotalCents @param {number} totalCents */
  function applyComputedMetricsLegacy(root, items, code, withCode, subtotalCents, totalCents) {
    /** @type {Record<string, { qty: number, weight: number, labour: number, subtotal: number }>} */
    var byProduct = {};
    var labourGrand = 0;
    var qtyGrand = 0;
    var weightGrand = 0;

    items.forEach(function (item) {
      var pid = String(item.product_id || '');
      if (!pid) return;
      if (!byProduct[pid]) byProduct[pid] = { qty: 0, weight: 0, labour: 0, subtotal: 0 };
      var m = byProduct[pid];
      var qty = Number(item.quantity || 0);
      var lineSubtotal = Number(item.final_line_price != null ? item.final_line_price : item.line_price || 0);
      var weights = weightStatsFromItem(item);
      var labour = labourSourceFromItem(item).cents;

      m.qty += Number.isFinite(qty) ? qty : 0;
      m.weight += Number.isFinite(weights.total) ? weights.total : 0;
      m.labour += Number.isFinite(labour) ? labour : 0;
      m.subtotal += Number.isFinite(lineSubtotal) ? lineSubtotal : 0;
      labourGrand += Number.isFinite(labour) ? labour : 0;
      qtyGrand += Number.isFinite(qty) ? qty : 0;
      weightGrand += Number.isFinite(weights.total) ? weights.total : 0;
    });

    root.querySelectorAll('[data-group-metrics]').forEach(function (metricEl) {
      if (!(metricEl instanceof HTMLElement)) return;
      var pid = metricEl.dataset.groupProductId || '';
      var m = byProduct[pid] || { qty: 0, weight: 0, labour: 0, subtotal: 0 };
      var qtyEl = qs('[data-group-total-qty]', metricEl);
      var weightEl = qs('[data-group-total-weight]', metricEl);
      var labourEl = qs('[data-group-total-labour]', metricEl);
      var subtotalEl = qs('[data-group-subtotal-cents]', metricEl);

      if (qtyEl) qtyEl.textContent = String(m.qty);
      if (weightEl) weightEl.textContent = m.weight > 0 ? m.weight.toFixed(2) + ' g' : '-';
      if (labourEl) labourEl.textContent = m.labour > 0 ? formatMoney(m.labour, code, withCode) : '-';
      if (subtotalEl) subtotalEl.textContent = formatMoney(m.subtotal, code, withCode);
    });

    var labourSummary = qs('[data-order-summary-labour]', root);
    if (labourSummary) {
      labourSummary.textContent = labourGrand > 0 ? formatMoney(labourGrand, code, withCode) : '-';
    }

    var subtotalEl = qs('[data-order-summary-subtotal]', root);
    if (subtotalEl) subtotalEl.textContent = formatMoney(subtotalCents, code, withCode);

    var totalQtyEl = qs('[data-order-summary-total-qty]', root);
    if (totalQtyEl) totalQtyEl.textContent = String(qtyGrand);

    var totalWeightEl = qs('[data-order-summary-total-weight]', root);
    if (totalWeightEl) totalWeightEl.textContent = weightGrand > 0 ? weightGrand.toFixed(2) + ' g' : '-';

    var estimated = totalCents;
    if (labourGrand > 0) {
      if (Math.abs(totalCents - subtotalCents) <= 1) {
        estimated = subtotalCents + labourGrand;
      } else if (Math.abs(totalCents - (subtotalCents + labourGrand)) <= 1) {
        estimated = totalCents;
      } else {
        estimated = totalCents;
      }
    }
    var totalEl = qs('[data-order-summary-total]', root);
    if (totalEl) totalEl.textContent = formatMoney(estimated, code, withCode);
  }

  /** @param {HTMLElement} root @param {Record<string, unknown>} cart @param {Record<string, unknown>} cfg */
  function applyCartToDom(root, cart, cfg) {
    var codeRaw = cart.currency;
    var code =
      typeof codeRaw === 'string'
        ? codeRaw
        : codeRaw && typeof codeRaw === 'object' && 'iso_code' in codeRaw
          ? String(/** @type {{ iso_code?: string }} */ (codeRaw).iso_code || '')
          : cfg.currencyCode;
    var withCode = !!cfg.currencyCodeEnabled;
    var subtotalCents = typeof cart.items_subtotal_price === 'number' ? cart.items_subtotal_price : 0;
    var totalCents = typeof cart.total_price === 'number' ? cart.total_price : 0;
    var count = typeof cart.item_count === 'number' ? cart.item_count : 0;
    var items = Array.isArray(cart.items) ? cart.items.map(function (it) {
      return /** @type {Record<string, unknown>} */ (it);
    }) : [];

    root.querySelectorAll('[data-b2b-cart-count]').forEach(function (countEl) {
      countEl.textContent = String(count);
    });

    var wrap = qs('[data-b2b-cart-count-wrap]', root);
    if (wrap) {
      wrap.hidden = count === 0;
      wrap.setAttribute('aria-hidden', count === 0 ? 'true' : 'false');
    }

    var mainTitle = qs('[data-b2b-main-title]', root);
    if (mainTitle) mainTitle.hidden = count === 0;
    var cartHeader = qs('[data-b2b-cart-header]', root);
    if (cartHeader) {
      cartHeader.hidden = count === 0;
      cartHeader.setAttribute('aria-hidden', count === 0 ? 'true' : 'false');
    }

    var itemByKey = {};
    items.forEach(function (item, idx) {
      var key = String(item.key || '');
      if (!key) return;
      itemByKey[key] = { item: item, line: idx + 1 };
    });

    root.querySelectorAll('[data-cart-item]').forEach(function (row) {
      if (!(row instanceof HTMLElement)) return;
      var key = row.dataset.lineKey || '';
      var packed = key ? itemByKey[key] : null;
      if (!packed) {
        var removeUi = getWeightUiEls(row);
        if (removeUi.panel instanceof HTMLElement) removeUi.panel.remove();
        if (removeUi.panelRow instanceof HTMLElement) {
          removeUi.panelRow.remove();
        } else {
          var legacyPanelRow = row.nextElementSibling;
          if (legacyPanelRow instanceof HTMLElement && legacyPanelRow.matches('[data-selected-weight-panel-row]')) {
            legacyPanelRow.remove();
          }
        }
        row.remove();
        return;
      }
      var item = packed.item;
      row.dataset.lineNumber = String(packed.line);
      row.dataset.quantity = String(item.quantity != null ? item.quantity : 1);
      row.dataset.productId = String(item.product_id != null ? item.product_id : row.dataset.productId || '');

      var qty = Number(item.quantity || 0);
      var qtyCell = qs('[data-row-qty]', row);
      if (qtyCell) qtyCell.textContent = String(Number.isFinite(qty) ? qty : 0);

      var stats = weightStatsFromItem(item);
      var totalWeightCell = qs('[data-row-total-weight]', row);

      var rawSelectedWeight = extractRawSelectedWeight(item);
      if (rawSelectedWeight != null && String(rawSelectedWeight).trim()) {
        row.dataset.selectedWeight = String(rawSelectedWeight).trim();
      } else {
        delete row.dataset.selectedWeight;
      }
      var normalized = normalizeSelectedWeights(rawSelectedWeight || '');
      renderSelectedWeightPanel(row, normalized, Number.isFinite(stats.total) ? stats.total : 0);

      if (totalWeightCell) {
        totalWeightCell.textContent = stats.total > 0 ? stats.total.toFixed(2) + ' g' : '-';
      }

      var labourDisplay = qs('[data-line-labour-display]', row);
      var labour = labourChargeForRow(item, row);
      if (labourDisplay) {
        labourDisplay.textContent = labour.hasValue
          ? formatMoney(labour.cents, String(code || 'USD'), withCode)
          : '-';
      }

      var unitPriceCell = qs('[data-line-unit-price]', row);
      var priceCell = qs('[data-line-price]', row);
      var goldRateEl = qs('[data-gold-price-rate]', row);
      var goldTipBtn = qs('[data-gold-price-row-tip]', row);
      if (unitPriceCell && !row.dataset.calculatedRowTotalCents) {
        unitPriceCell.textContent = 'Loading…';
      }
      if (priceCell && !row.dataset.calculatedRowTotalCents) {
        priceCell.textContent = 'Loading…';
      }
      if (goldRateEl instanceof HTMLElement && !row.dataset.calculatedRowTotalCents) {
        goldRateEl.textContent = 'Loading…';
        if (goldTipBtn instanceof HTMLElement) {
          goldTipBtn.hidden = true;
          goldTipBtn.setAttribute('aria-hidden', 'true');
        }
      }
    });
    syncSelectedWeightPanelPlacement(root);

    root.querySelectorAll('[data-product-group]').forEach(function (groupEl) {
      if (!(groupEl instanceof HTMLElement)) return;
      var pid = groupEl.dataset.productId || '';
      if (!pid) return;
      var hasAny = !!groupEl.querySelector('[data-cart-item][data-product-id="' + escCssIdent(pid) + '"]');
      if (!hasAny) groupEl.remove();
    });

    reconcileProductDetails(root);

    syncLineAddedAtMetadata(root, items, cfg);

    root.classList.toggle('b2b-cart-review--has-items', count > 0);
    var layout = qs('[data-b2b-cart-layout]', root);
    var emptyShell = qs('[data-b2b-empty-shell]', root);
    var summarySidebar = qs('[data-b2b-summary-sidebar]', root);
    if (count === 0) {
      if (layout) layout.hidden = true;
      if (emptyShell) emptyShell.hidden = false;
      if (summarySidebar) {
        summarySidebar.hidden = true;
        summarySidebar.setAttribute('aria-hidden', 'true');
      }
    } else {
      if (layout) layout.hidden = false;
      if (emptyShell) emptyShell.hidden = true;
      if (summarySidebar) {
        summarySidebar.hidden = false;
        summarySidebar.removeAttribute('aria-hidden');
      }
    }

    applyComputedMetrics(root, items, cfg);
    updateSalesRepGate(root, cfg);
  }

  /** @param {HTMLElement} root @param {string} text */
  function showCartFeedback(root, text) {
    var el = qs('[data-b2b-cart-feedback]', root);
    if (!el) return;
    if (!text) {
      el.hidden = true;
      el.textContent = '';
      return;
    }
    el.textContent = text;
    el.hidden = false;
    window.setTimeout(function () {
      if (el.textContent === text) el.hidden = true;
    }, 4500);
  }

  /** @param {Record<string, unknown>} cart @param {string} sourceId */
  function publishCartCountUpdate(cart, sourceId) {
    var bridge = typeof window !== 'undefined' ? window.aoneCartCount : null;
    if (bridge && typeof bridge.publish === 'function') {
      bridge.publish(cart, sourceId || 'b2b-cart-review');
      return;
    }
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: sourceId || 'b2b-cart-review',
          data: { itemCount: typeof cart.item_count === 'number' ? cart.item_count : 0 },
        },
      })
    );
  }

  /** @param {HTMLElement} root @param {Record<string, unknown>} cfg */
  function init(root, cfg) {
    var syncBtn = qs('[data-sync-button]', root);

    updateSalesRepGate(root, cfg);
    window.addEventListener('storage', function () {
      updateSalesRepGate(root, cfg);
    });

    /** @param {number} line @param {number} qty */
    async function changeLine(line, qty) {
      root.classList.add('b2b-cart-review--updating');
      root.setAttribute('aria-busy', 'true');
      setProductDetailsButtonsDisabled(root, true);
      try {
        var body = JSON.stringify({ line: line, quantity: qty });
        var r = await fetchJson(String(cfg.cartChangeJsUrl), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: body,
        });
        /** @type {Record<string, unknown>} */
        var cart = /** @type {Record<string, unknown>} */ (r.data);
        if (!r.res.ok) {
          var emsg =
            (typeof cart.message === 'string' && cart.message) ||
            (cart.description && String(cart.description)) ||
            'Could not update cart.';
          showCartFeedback(root, emsg);
          return null;
        }
        if (cart.errors) {
          showCartFeedback(root, typeof cart.errors === 'string' ? cart.errors : 'Cart could not be updated.');
          return null;
        }
        applyCartToDom(root, cart, cfg);
        publishCartCountUpdate(cart, 'b2b-cart-review:line-change');
        return cart;
      } catch {
        showCartFeedback(root, 'Network error while updating cart.');
        return null;
      } finally {
        root.classList.remove('b2b-cart-review--updating');
        root.removeAttribute('aria-busy');
        setProductDetailsButtonsDisabled(root, false);
      }
    }

    /**
     * @param {Record<string, number>} updates
     * @param {{rootBusy?: boolean}} [opts]
     */
    async function updateCartByKeys(updates, opts) {
      if (!updates || !Object.keys(updates).length) return null;
      if (!opts || opts.rootBusy !== false) {
        root.classList.add('b2b-cart-review--updating');
        root.setAttribute('aria-busy', 'true');
        setProductDetailsButtonsDisabled(root, true);
      }
      try {
        var updateUrl = String(cfg.cartUpdateJsUrl || '/cart/update.js');
        var r = await fetchJson(updateUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates: updates }),
        });
        var cart = /** @type {Record<string, unknown>} */ (r.data);
        if (!r.res.ok || cart.errors) {
          var err =
            (typeof cart.message === 'string' && cart.message) ||
            (typeof cart.errors === 'string' && cart.errors) ||
            'Cart could not be updated.';
          showCartFeedback(root, err);
          return null;
        }
        applyCartToDom(root, cart, cfg);
        publishCartCountUpdate(cart, 'b2b-cart-review:key-update');
        return cart;
      } catch {
        showCartFeedback(root, 'Network error while updating cart.');
        return null;
      } finally {
        if (!opts || opts.rootBusy !== false) {
          root.classList.remove('b2b-cart-review--updating');
          root.removeAttribute('aria-busy');
          setProductDetailsButtonsDisabled(root, false);
        }
      }
    }

    /** @param {string} productId @param {HTMLButtonElement | null} [trigger] */
    async function removeGroup(productId, trigger) {
      var rows = root.querySelectorAll('[data-cart-item][data-product-id="' + escCssIdent(productId) + '"]');
      if (!rows.length) return;
      /** @type {Record<string, number>} */
      var updates = {};
      rows.forEach(function (row) {
        if (!(row instanceof HTMLElement)) return;
        var key = row.dataset.lineKey || '';
        if (key) updates[key] = 0;
      });
      if (!Object.keys(updates).length) return;
      if (trigger) {
        trigger.disabled = true;
        trigger.setAttribute('aria-busy', 'true');
      }
      try {
        var cart = await updateCartByKeys(updates);
        if (!cart) {
          showCartFeedback(root, 'Could not remove selected product.');
        } else {
          showToast(cfg, String(cfg.removeSuccessMessage || 'The item is removed successfully'), 'success');
        }
      } finally {
        if (trigger) {
          trigger.disabled = false;
          trigger.removeAttribute('aria-busy');
        }
      }
    }

    /** @param {string} lineKey @param {HTMLButtonElement} trigger */
    async function removeLineByKey(lineKey, trigger) {
      var key = String(lineKey || '').trim();
      if (!key) return;
      if (trigger.disabled) return;
      trigger.disabled = true;
      trigger.setAttribute('aria-busy', 'true');
      try {
        /** @type {Record<string, number>} */
        var updates = {};
        updates[key] = 0;
        var cart = await updateCartByKeys(updates);
        if (!cart) {
          showCartFeedback(root, 'Could not remove selected variant.');
        } else {
          showToast(cfg, String(cfg.removeSuccessMessage || 'The item is removed successfully'), 'success');
        }
      } finally {
        trigger.disabled = false;
        trigger.removeAttribute('aria-busy');
      }
    }

    var debouncedInput = debounced(function (/** @type {HTMLInputElement} */ input) {
      var row = input.closest('[data-cart-item]');
      if (!(row instanceof HTMLElement)) return;
      var q = parseInt(input.value, 10);
      var fq = Number.isNaN(q) ? 1 : Math.max(1, q);
      input.value = String(fq);
      var line = parseInt(row.dataset.lineNumber || '0', 10);
      if (line) changeLine(line, fq);
    }, 440);

    root.addEventListener('submit', function (e) {
      var form = e.target;
      if (!(form instanceof HTMLFormElement) || !form.matches('[data-b2b-clear-cart-form]')) return;
      if (!window.confirm('Clear all items from your cart?')) {
        e.preventDefault();
      }
    });

    root.addEventListener('click', function (e) {
      var t = e.target;
      if (!(t instanceof Element)) return;
      var weightToggle = t.closest('[data-selected-weight-toggle]');
      var weightHide = t.closest('[data-selected-weight-hide]');
      var productDetailsToggle = t.closest('[data-product-details-toggle]');
      var minus = t.closest('[data-quantity-minus]');
      var plus = t.closest('[data-quantity-plus]');
      var removeBtn = t.closest('[data-remove-item]');
      var removeGroupBtn = t.closest('[data-remove-group]');

      if (weightToggle && weightToggle instanceof HTMLButtonElement) {
        var rowForToggle = weightToggle.closest('[data-cart-item]');
        if (!(rowForToggle instanceof HTMLElement)) return;
        var nextExpanded = weightToggle.getAttribute('aria-expanded') !== 'true';
        if (nextExpanded) {
          var normalizedForToggle = normalizeSelectedWeights(rowForToggle.dataset.selectedWeight || '');
          var totalWeightCellForToggle = qs('[data-row-total-weight]', rowForToggle);
          var totalWeightForToggle =
            parseWeightNumber(totalWeightCellForToggle ? totalWeightCellForToggle.textContent : '') || 0;
          renderSelectedWeightPanel(rowForToggle, normalizedForToggle, totalWeightForToggle);
        }
        setSelectedWeightPanelState(rowForToggle, nextExpanded);
        return;
      }

      if (weightHide && weightHide instanceof HTMLButtonElement) {
        var panel = weightHide.closest('[data-selected-weight-panel]');
        if (!(panel instanceof HTMLElement)) return;
        var rowForHide = panel.closest('[data-cart-item]');
        if (!(rowForHide instanceof HTMLElement)) {
          var panelRowForHide = panel.closest('[data-selected-weight-panel-row]');
          rowForHide =
            panelRowForHide && panelRowForHide.previousElementSibling instanceof HTMLElement
              ? panelRowForHide.previousElementSibling
              : null;
        }
        if (!(rowForHide instanceof HTMLElement) || !rowForHide.matches('[data-cart-item]')) return;
        setSelectedWeightPanelState(rowForHide, false, { restoreFocus: true });
        return;
      }

      if (productDetailsToggle && productDetailsToggle instanceof HTMLButtonElement) {
        if (productDetailsToggle.disabled) return;
        var productCard = productDetailsToggle.closest('[data-product-group]');
        if (!(productCard instanceof HTMLElement)) return;
        var nextExpanded = productDetailsToggle.getAttribute('aria-expanded') !== 'true';
        setProductDetailsState(productCard, nextExpanded);
        return;
      }

      if (minus || plus) {
        var wrap = t.closest('[data-cart-item]');
        if (!(wrap instanceof HTMLElement)) return;
        var input = qs('[data-quantity-input]', wrap);
        if (!(input instanceof HTMLInputElement)) return;
        var q = parseInt(input.value, 10) || 1;
        if (minus) q = Math.max(1, q - 1);
        if (plus) q = q + 1;
        input.value = String(q);
        var line = parseInt(wrap.dataset.lineNumber || '0', 10);
        if (line) changeLine(line, q);
        return;
      }

      if (removeBtn) {
        var w = removeBtn.closest('[data-cart-item]');
        if (!(w instanceof HTMLElement)) return;
        if (!(removeBtn instanceof HTMLButtonElement)) return;
        var removeButton = removeBtn;
        var lineKey = String(w.dataset.lineKey || '').trim();
        if (!lineKey) return;
        if (removeButton.disabled) return;
        if (!cfg.confirmRemove) {
          removeLineByKey(lineKey, removeButton);
          return;
        }
        /** @type {HTMLElement | null} */
        var modalEl = null;
        var mid = cfg.removeConfirmModalId != null ? String(cfg.removeConfirmModalId) : '';
        if (mid) modalEl = document.getElementById(mid);
        var AC = typeof window !== 'undefined' ? window.AoneConfirmationModal : undefined;
        if (!modalEl || !AC || typeof AC.open !== 'function') {
          removeLineByKey(lineKey, removeButton);
          return;
        }
        if (removeButton.dataset.removeConfirmPending === '1') return;
        removeButton.dataset.removeConfirmPending = '1';
        AC.open(modalEl).then(function (ok) {
          if (ok) removeLineByKey(lineKey, removeButton);
        }).finally(function () {
          delete removeButton.dataset.removeConfirmPending;
        });
        return;
      }

      if (removeGroupBtn && removeGroupBtn instanceof HTMLElement) {
        var productId = String(removeGroupBtn.dataset.groupProductId || '').trim();
        if (!productId) return;
        var groupBtn = removeGroupBtn instanceof HTMLButtonElement ? removeGroupBtn : null;
        if (groupBtn && groupBtn.disabled) return;
        if (!cfg.confirmRemove) {
          removeGroup(productId, groupBtn || undefined);
          return;
        }
        var modal = null;
        var modalId = cfg.removeConfirmModalId != null ? String(cfg.removeConfirmModalId) : '';
        if (modalId) modal = document.getElementById(modalId);
        var Confirm = typeof window !== 'undefined' ? window.AoneConfirmationModal : undefined;
        if (!modal || !Confirm || typeof Confirm.open !== 'function') {
          removeGroup(productId, groupBtn || undefined);
          return;
        }
        if (groupBtn && groupBtn.dataset.removeConfirmPending === '1') return;
        if (groupBtn) groupBtn.dataset.removeConfirmPending = '1';
        Confirm.open(modal).then(function (ok) {
          if (ok) removeGroup(productId, groupBtn || undefined);
        }).finally(function () {
          if (groupBtn) delete groupBtn.dataset.removeConfirmPending;
        });
      }
    });

    root.addEventListener('input', function (e) {
      var t = e.target;
      if (t instanceof HTMLInputElement && t.matches('[data-quantity-input]')) debouncedInput(t);
    });

    root.addEventListener('focusout', function (e) {
      var t = e.target;
      if (!(t instanceof HTMLInputElement) || !t.matches('[data-quantity-input]')) return;
      var rt = e.relatedTarget;
      if (rt instanceof Element && rt.closest('[data-qty-controls]')) return;
      var q = parseInt(t.value, 10);
      if (Number.isNaN(q) || q < 1) {
        t.value = '1';
        q = 1;
      }
      debouncedInput.cancel();
      var row = t.closest('[data-cart-item]');
      if (!(row instanceof HTMLElement)) return;
      var line = parseInt(row.dataset.lineNumber || '0', 10);
      if (line) changeLine(line, q);
    });

    /** @param {Record<string, unknown>} cart @param {string} sourceId */
    function syncCartReviewFromServer(cart, sourceId) {
      var count = typeof cart.item_count === 'number' ? cart.item_count : 0;
      var layout = qs('[data-b2b-cart-layout]', root);
      if (count > 0 && !layout) {
        window.location.reload();
        return;
      }
      applyCartToDom(root, cart, cfg);
      publishCartCountUpdate(cart, sourceId);
    }

    fetchJson(String(cfg.cartJsUrl || '/cart.js'))
      .then(function (result) {
        if (result && result.res && result.res.ok) {
          syncCartReviewFromServer(
            /** @type {Record<string, unknown>} */ (result.data),
            'b2b-cart-review:initial-load'
          );
        }
      })
      .catch(function () {
        /* keep server-rendered fallback */
      });

    document.addEventListener('cart:update', function (ev) {
      if (!(root instanceof HTMLElement) || !root.isConnected) return;
      var detail = ev && typeof ev === 'object' && 'detail' in ev ? ev.detail : null;
      if (!detail || typeof detail !== 'object') return;
      if (detail.sourceId && String(detail.sourceId).indexOf('b2b-cart-review') === 0) return;
      var resource = detail.resource;
      if (resource && typeof resource === 'object' && typeof resource.item_count === 'number') {
        syncCartReviewFromServer(/** @type {Record<string, unknown>} */ (resource), 'b2b-cart-review:cart-update');
        return;
      }
      fetchJson(String(cfg.cartJsUrl || '/cart.js'))
        .then(function (result) {
          if (result && result.res && result.res.ok) {
            syncCartReviewFromServer(
              /** @type {Record<string, unknown>} */ (result.data),
              'b2b-cart-review:cart-update'
            );
          }
        })
        .catch(function () {
          /* ignore */
        });
    });

    root.querySelectorAll('[data-cart-item]').forEach(function (row) {
      if (!(row instanceof HTMLElement)) return;
      var normalized = normalizeSelectedWeights(row.dataset.selectedWeight || '');
      var totalWeightCell = qs('[data-row-total-weight]', row);
      var totalWeight = parseWeightNumber(totalWeightCell ? totalWeightCell.textContent : '') || 0;
      renderSelectedWeightPanel(row, normalized, totalWeight);
      setSelectedWeightPanelState(row, false);
    });
    syncSelectedWeightPanelPlacement(root);
    var weightLayoutMq = window.matchMedia('(max-width: 767px)');
    var onWeightLayoutChange = function () {
      if (!(root instanceof HTMLElement) || !root.isConnected) return;
      syncSelectedWeightPanelPlacement(root);
    };
    if (typeof weightLayoutMq.addEventListener === 'function') {
      weightLayoutMq.addEventListener('change', onWeightLayoutChange);
    } else if (typeof weightLayoutMq.addListener === 'function') {
      weightLayoutMq.addListener(onWeightLayoutChange);
    }
    reconcileProductDetails(root);

    var goldPricing = getGoldPricing();
    if (goldPricing) {
      goldPricing.initFromDom(document);
      var refreshPricing = function () {
        recalculateCartPricing(root, cfg, root.__b2bLastCartItems || []);
      };
      goldPricing.waitForReady().then(refreshPricing);
      document.addEventListener(goldPricing.UPDATE_EVENT || 'aone:gold-prices-updated', refreshPricing);
    } else {
      recalculateCartPricing(root, cfg);
    }

    if (!(syncBtn instanceof HTMLButtonElement)) return;

    /** @param {Record<string, unknown>} cart @param {{id:number|string,name:string}} customer */
    async function performSync(cart, customer) {
      var pricingError = validateCartPricing(cart, cfg, root);
      if (pricingError) {
        showToast(cfg, pricingError, 'error');
        return;
      }

      var apiBase = String(cfg.apiBaseUrl || '').trim();
      var syncPath = String(cfg.syncPath || '/public/theme/cart-sync');
      if (!apiBase) {
        var missingApi = String(cfg.syncErrorFallback || 'Unable to sync order');
        showToast(cfg, missingApi, 'error');
        return;
      }
      if (!cfg.salesRepEmail) {
        showToast(cfg, 'Please log in as a sales representative to sync this order.', 'error');
        return;
      }

      var syncUrl = joinUrl(apiBase, syncPath);
      var items = Array.isArray(cart.items) ? cart.items : [];
      var goldPricing = getGoldPricing();
      var body = {
        salesRepEmail: String(cfg.salesRepEmail),
        customerId: Number(customer.id),
        // TODO: Backend must recalculate totals from captured gold rate, selected weight, material, and labour - do not trust browser totals alone.
        computed_total_cents:
          typeof root.__b2bComputedOrderTotalCents === 'number' ? root.__b2bComputedOrderTotalCents : null,
        items: items.map(function (raw) {
          var item = /** @type {Record<string, unknown>} */ (raw);
          var props =
            item.properties && typeof item.properties === 'object'
              ? /** @type {Record<string, unknown>} */ (item.properties)
              : {};
          var material = jewelleryMaterialFromItem(item);
          var weights = weightStatsFromItem(item);
          var labour = labourChargeForRow(item);
          /** @type {Record<string, unknown>} */
          var payloadItem = {
            variantId: item.variant_id,
            quantity: item.quantity,
            sku: item.sku != null ? String(item.sku) : props.SKU != null ? String(props.SKU) : '',
            properties: props,
            captured_gold_price_per_troy_oz:
              props._gold_price_per_troy_oz != null ? String(props._gold_price_per_troy_oz) : '',
            captured_gold_price_material:
              props._gold_price_material != null ? String(props._gold_price_material) : material,
            captured_gold_price_captured_at:
              props._gold_price_captured_at != null ? String(props._gold_price_captured_at) : '',
            selected_weight_total_grams: weights.total,
            labour_charge_cents: labour.hasValue ? labour.cents : 0,
            labour_per_gram: labour.hasValue ? labour.cents / 100 : 0,
            gold_karat: goldPricing && goldPricing.normalizeGoldKarat ? goldPricing.normalizeGoldKarat(material) : material,
          };
          if (goldPricing) {
            var pricingResult = goldPricing.calculateRowPricing({
              properties: props,
              rowMaterial: material,
              totalWeightGrams: weights.total,
              labourCents: labour.hasValue ? labour.cents : 0,
              labourResolved: labour.hasValue,
              cartCurrency: String(cfg.currencyCode || goldPricing.getActiveCurrencyCode()),
              preferLive: true,
              allowFallback: false,
            });
            payloadItem.computed_row_total_cents =
              pricingResult.status === 'ok' && pricingResult.rowTotalCents != null
                ? pricingResult.rowTotalCents
                : null;
            payloadItem.live_gold_price_per_gram =
              pricingResult.status === 'ok' && pricingResult.goldPricePerGram != null
                ? pricingResult.goldPricePerGram
                : null;
            payloadItem.unit_price_per_gram =
              pricingResult.status === 'ok' && pricingResult.unitPricePerGram != null
                ? pricingResult.unitPricePerGram
                : null;
            payloadItem.gold_rate_updated_at =
              pricingResult.goldRateUpdatedAt != null ? String(pricingResult.goldRateUpdatedAt) : '';
            if (pricingResult.labourPerGram != null) {
              payloadItem.labour_per_gram = pricingResult.labourPerGram;
            }
          }
          return payloadItem;
        }),
      };

      var labelEl = qs('[data-sync-button-label]', root);
      var defaultLabel = cfg.syncButtonTextDefault != null ? String(cfg.syncButtonTextDefault) : 'SYNC ORDER';
      syncBtn.disabled = true;
      syncBtn.classList.add('is-loading');
      if (labelEl) labelEl.textContent = String(cfg.syncLoadingText || 'SYNCING...');

      try {
        var result = await fetchApiJson(syncUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (!result.res.ok) {
          var errMsg =
            typeof result.data.message === 'string'
              ? result.data.message
              : String(cfg.syncErrorFallback || 'Unable to sync order');
          showToast(cfg, errMsg, 'error');
          return;
        }

        var successMsg = String(cfg.syncSuccessMessage || 'Order synced successfully');
        showToast(cfg, successMsg, 'success');

        if (cfg.clearCartAfterSync) {
          var clearResult = await fetchJson(String(cfg.cartClearJsUrl || '/cart/clear.js'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: '{}',
          });
          var clearedCart = /** @type {Record<string, unknown>} */ (clearResult.data);
          if (clearResult.res.ok && !clearedCart.errors) {
            applyCartToDom(root, clearedCart, cfg);
          }
        }
      } catch {
        showToast(cfg, String(cfg.syncErrorFallback || 'Unable to sync order'), 'error');
      } finally {
        syncBtn.disabled = false;
        syncBtn.classList.remove('is-loading');
        if (labelEl) labelEl.textContent = defaultLabel;
        updateSalesRepGate(root, cfg);
      }
    }

    syncBtn.addEventListener('click', async function () {
      if (cfg.adminDisableSync) {
        showToast(cfg, String(cfg.adminDisableSyncText || 'Admin disabled'), 'error');
        return;
      }

      if (!isSalesRepOk(cfg)) {
        showToast(cfg, String(cfg.loginWarningFallback || 'Please log in as a sales representative to sync this order.'), 'error');
        return;
      }

      var cartResult = await fetchJson(String(cfg.cartJsUrl || '/cart.js'));
      var cart = /** @type {Record<string, unknown>} */ (cartResult.data);
      if (!cartResult.res.ok) {
        showToast(cfg, String(cfg.syncErrorFallback || 'Unable to sync order'), 'error');
        return;
      }

      var validationError = validateCartLinesForSync(cart);
      if (validationError) {
        showToast(cfg, validationError, 'error');
        return;
      }

      var pricingError = validateCartPricing(cart, cfg, root);
      if (pricingError) {
        showToast(cfg, pricingError, 'error');
        return;
      }

      var Picker = typeof window !== 'undefined' ? window.AonegndCustomerPicker : undefined;
      if (!Picker || typeof Picker.open !== 'function') {
        showToast(cfg, String(cfg.syncErrorFallback || 'Unable to sync order'), 'error');
        return;
      }

      var selected = await Picker.open({
        apiBaseUrl: cfg.apiBaseUrl,
        customersPath: cfg.customersPath,
        salesRepEmail: cfg.salesRepEmail,
        title: cfg.customerPickerTitle,
        doneLabel: cfg.customerPickerDoneLabel,
        cancelLabel: cfg.customerPickerCancelLabel,
        searchPlaceholder: cfg.customerPickerSearchPlaceholder,
      });

      if (!selected || selected.id == null) return;
      await performSync(cart, selected);
    });
  }

  function boot(scope) {
    var rootEl = scope instanceof HTMLElement ? scope : document.documentElement;
    var candidates = rootEl.querySelectorAll('[data-b2b-cart-review]');
    for (var i = 0; i < candidates.length; i++) {
      var root = candidates[i];
      if (!(root instanceof HTMLElement)) continue;
      if (root.dataset.b2bCartReviewInit === '1') continue;
      var section = root.closest('.shopify-section');
      var cfgEl =
        section && section instanceof HTMLElement ? qs('[data-b2b-cart-review-config]', section) : null;
      if (!(cfgEl instanceof HTMLElement)) cfgEl = qs('[data-b2b-cart-review-config]');
      if (!(cfgEl instanceof HTMLElement)) continue;
      /** @type {Record<string, unknown>} */
      var cfg;
      try {
        cfg = JSON.parse(cfgEl.textContent || '{}');
      } catch {
        continue;
      }
      root.dataset.b2bCartReviewInit = '1';
      init(root, cfg);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      boot(document.documentElement);
    });
  } else {
    boot(document.documentElement);
  }

  document.addEventListener('shopify:section:load', function (ev) {
    if (ev.target instanceof HTMLElement) boot(ev.target);
  });
})();
